// ====================================================================
// ziven-core forum DiscussionReactionSummary - 列表项 reaction 总数 (desktop 模式)
// ====================================================================
//
// 渲染 DiscussionListItem infoItems 末尾追加的 reaction summary 段
//   首个 post 收到的 reaction 表情 + 总数.
//
// (辉哥 11:05): "只显示最热 1 个 reaction" (waterhole condensed 风格)
// (辉哥 11:16 反馈): 撤掉旧逻辑, 改回渲染所有 reaction types
//   - 从 entries 找 count 最大的 reaction (topReaction)
//   - 只渲染 topReaction 1 个 emoji
//   - 数字 = 所有 reaction count 求和
//   11:16 辉哥反馈: 明明两个 reaction 就该展示两个 (他 d/12 firstPost reactionCounts {'1': 1, '6': 1})
//
// 撤掉 "只显示最热 1 个" 逻辑, 改回渲染所有 reaction types
//   - 每个非 0 count 的 reaction 都渲染 emoji, 视觉上紧贴排列 (flex gap: 0)
//   - 数字 = 所有 reaction count 求和
//
// 跟 ziven-reactions PostReactionCounts 区别:
//   - PostReactionCounts (ziven-reactions 帖子页 footer 段):
//     单个 Post 自己的 reaction 列表 + count, 用户点了哪个标 active
//   - DiscussionReactionSummary (本组件):
//     Discussion 列表项的 reaction 摘要, 跨 Post 聚合概念
//
// (Fix A 配合 ziven-reactions extend.php expose reactionCounts, 辉哥 2026-09-21 20:40 拍板):
//   根因 (Mavis Worker 9/21 20:32 调查): Flarum 2.0 addDefaultInclude 是 fallback 语义,
//     客户端带 include 参数时服务端忽略 defaultInclude → firstPost 拿不到
//   修法: 三路 fallback 拿 reactionCounts (discussion.reactionCounts() / attribute / firstPost),
//     不再强制依赖 firstPost. 渲染条件从 firstPost truthy 改成 entries/totalCount 有内容
//   配套: 删 line 100-107 firstPost 防御 (Fix A 后 firstPost 可能 null 但 reactionCounts 仍可渲染)
//
// 防御:
//   - ziven-reactions 没装 → 三路 fallback 都拿不到 → return null (silently skip)
//   - 任何 reaction id 在 store 找不到 → filter 跳过
//   - reactionCounts 全是 0 → return null (不显示)
// ====================================================================
import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import { TWEMOJI_CDN, REACTION_TWEMOJI } from '../utils/reactionTwemoji';
function renderReactionEmoji(reaction) {
  const identifier = reaction.identifier();
  const display = reaction.display() || identifier;

  // icon 类型: 走 FontAwesome <i> (ziven-reactions 也这么处理)
  if (reaction.type() === 'icon') {
    return m("i", {
      class: 'ziven-core-discussion-reaction-emoji ' + identifier,
      "data-reaction": identifier,
      "aria-hidden": "true",
      title: display
    });
  }

  // emoji 类型: 优先 Twemoji CDN <img>, 未知 identifier 兜底 unicode 字符
  const meta = REACTION_TWEMOJI[identifier];
  if (meta != null && meta.codepoint) {
    return m("img", {
      className: "ziven-core-discussion-reaction-emoji emoji",
      src: TWEMOJI_CDN + meta.codepoint + '.png',
      alt: display,
      title: display,
      "data-reaction": identifier,
      draggable: "false",
      loading: "lazy"
    });
  }

  // 兜底: unicode 字符 (未知 reaction)
  return m("span", {
    className: "ziven-core-discussion-reaction-emoji",
    "data-reaction": identifier,
    title: display
  }, (meta == null ? void 0 : meta.unicode) || display);
}

// (Fix A): 三路 fallback 拿 reactionCounts (跟 DiscussionListItemMobile.jsx 同款)
function getReactionCounts(discussion) {
  if (!discussion) return {};
  if (typeof discussion.reactionCounts === 'function') {
    const rc = discussion.reactionCounts();
    if (rc && typeof rc === 'object') return rc;
  }
  if (typeof discussion.attribute === 'function') {
    const rc = discussion.attribute('reactionCounts');
    if (rc && typeof rc === 'object' && Object.keys(rc).length > 0) return rc;
  }
  const firstPost = discussion.firstPost();
  if (firstPost && typeof firstPost.reactionCounts === 'function') {
    const rc = firstPost.reactionCounts();
    if (rc && typeof rc === 'object') return rc;
  }
  return {};
}
export default class DiscussionReactionSummary extends Component {
  view() {
    const discussion = this.attrs.discussion;
    if (!discussion) return null;

    // (Fix A): 不再强制要求 firstPost 存在, 优先读 discussion 级 attribute
    const reactionCounts = getReactionCounts(discussion);
    if (!reactionCounts) return null;

    // 收集 (reaction, count) 列表, 跳过 0 count + 找不到 reaction 定义的
    const entries = Object.keys(reactionCounts).map(id => {
      const reaction = app.store.getById('reactions', id);
      if (!reaction) return null;
      const count = reactionCounts[id];
      if (!count || count === 0) return null;
      return {
        reaction,
        count
      };
    }).filter(Boolean);
    if (entries.length === 0) return null;

    // 总数 = 所有 reaction count 求和
    const totalCount = entries.reduce((sum, e) => sum + e.count, 0);
    if (totalCount === 0) return null;

    // 渲染所有 reaction types (辉哥 11:16 反馈"明明两个 reaction 就该展示两个")
    //   不再 "只显示最热 1 个" 逻辑, 每个非 0 count 的 reaction 都渲染
    //   视觉: 多 emoji 紧贴排列 (gap: 0) + 总数
    return m("span", {
      className: "ziven-core-discussion-reaction-summary",
      "data-count": totalCount
    }, entries.map(e => m("span", {
      key: e.reaction.id(),
      className: "ziven-core-discussion-reaction-entry"
    }, renderReactionEmoji(e.reaction))), m("span", {
      className: "ziven-core-discussion-reaction-total"
    }, totalCount));
  }
}
flarum.reg.add('ziiven-ziven-core', 'forum/components/DiscussionReactionSummary', DiscussionReactionSummary);